import { useEffect } from 'react';
import type { QaReviewedItem, QaVerdictValue } from '../api/types';
import { bustRender } from '../api/cacheBust';
import { Portal } from './Portal';

interface QaZoomModalProps {
  item: QaReviewedItem;
  busy?: boolean;
  onClose: () => void;
  onOverride: (verdict: QaVerdictValue) => void;
}

export function QaZoomModal({ item, busy, onClose, onOverride }: QaZoomModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Portal>
      {/* z-50 to overlay the page chrome (matches CutoutZoomModal). */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-ff-ink/70 p-3 backdrop-blur-sm sm:p-8"
        onClick={onClose}
      >
        <div
          className="max-h-[92vh] w-full max-w-6xl overflow-auto rounded-2xl border border-white/40 bg-white shadow-hover"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Image comparison for ${item.product_id}`}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-ff-sand/60">
            <div>
              <div className="font-display text-xl text-ff-forest">{item.title ?? '—'}</div>
              <div className="font-mono text-xs text-ff-mute">{item.product_id}</div>
            </div>
            <button onClick={onClose} type="button" className="ff-btn-ghost">
              Close (Esc)
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-2">
                Source image
              </div>
              <img src={item.source_image_url} alt="Source" className="w-full bg-ff-sand/20" />
            </div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-2">
                Final ad
              </div>
              <img src={bustRender(item.ad_image_url, item.render_at)} alt="Final ad" className="w-full" />
              {item.ai_reason && (
                <div className="mt-2 text-xs text-ff-mute italic">
                  AI verdict: {item.ai_verdict} — {item.ai_reason}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 px-6 py-4 border-t border-ff-sand/60">
            <button
              onClick={() => onOverride('pass')}
              type="button"
              disabled={busy}
              className="ff-btn-primary disabled:opacity-50"
            >
              ✓ Approve
            </button>
            <button
              onClick={() => onOverride('fail')}
              type="button"
              disabled={busy}
              className="ff-btn-danger disabled:opacity-50"
            >
              ✗ Disapprove
            </button>
            <span className="font-mono text-[11px] uppercase tracking-widish text-ff-mute ml-auto">
              effective: {item.effective_verdict ?? '—'}
            </span>
          </div>
        </div>
      </div>
    </Portal>
  );
}
